/**
 * @module services/orders-crm
 * @description UC-REG-162 — Servicio HTTP del CRM de órdenes.
 * Consume los endpoints de /indication-crm del backend.
 */

import api from "./api";

const ordersCrmService = {
  /**
   * @description Bandeja del CRM. Filtros opcionales.
   * @param {Object} [params] - status (csv), type (csv), doctor,
   *   coverage, from, to, search, limit, offset.
   * @returns {Promise<{docs: Array, total: number, offset: number, limit: number}>}
   */
  list(params = {}) {
    return api.get("/indication-crm", { params }).then((r) => r.data);
  },

  /**
   * @description Lista de prestadores emisores para el filtro.
   * @returns {Promise<Array<{_id, firstName, lastName}>>}
   */
  listProviders() {
    return api.get("/indication-crm/providers").then((r) => r.data);
  },

  /**
   * @description Lista de coberturas para el filtro. Incluye la entrada
   *              especial `{ key: 'particular', label: 'Particular' }`.
   * @returns {Promise<Array<{key: string, label: string}>>}
   */
  listCoverages() {
    return api.get("/indication-crm/coverages").then((r) => r.data);
  },

  /**
   * @description Contadores para el header del CRM.
   * @param {Object} [params] - from, to (mismo rango que la bandeja).
   * @returns {Promise<{active: number, takenThisWeek: number, closed: number}>}
   */
  counts(params = {}) {
    return api.get("/indication-crm/counts", { params }).then((r) => r.data);
  },

  /**
   * @description Cambia el estado del CRM de una orden. Agrega entry al
   *              history con timestamp + user que hizo el cambio + note.
   * @param {string} id
   * @param {string} status - Uno de: new, contacted, appointment-proposed,
   *   appointment-taken, closed.
   * @param {string} [note] - Nota opcional (típicamente al cerrar caso).
   * @returns {Promise<Object>} indication actualizada con populate completo.
   */
  updateStatus(id, status, note) {
    return api
      .post(`/indication-crm/${id}/status`, { status, note })
      .then((r) => r.data);
  },

  /**
   * @description Dispara WhatsApp al paciente vía 2chat (motor existente).
   *              Si el estado del caso es 'new', el backend lo pasa a
   *              'contacted' automáticamente y agrega entry al history.
   * @param {string} id
   * @param {string} message - Texto del WhatsApp (max 1500 chars).
   * @returns {Promise<Object>} Indication actualizada.
   */
  contactWhatsapp(id, message) {
    return api
      .post(`/indication-crm/${id}/contact-whatsapp`, { message })
      .then((r) => r.data);
  },

  /**
   * @description Descarga CSV compatible con Excel de los resultados
   *              filtrados. Reusa los mismos params que list().
   * @param {Object} params - Mismos filtros que list().
   * @returns {Promise<Blob>}
   */
  exportCsv(params = {}) {
    return api
      .get("/indication-crm/export", { params, responseType: "blob" })
      .then((r) => r.data);
  },
};

export default ordersCrmService;
